import { FunctionComponent, useState, useEffect } from "react"
import { Container, Row } from "react-grid-system"
import { HorizontalSpacer } from "../../atoms/spacers"
import styled from "styled-components"
import { CheckCircleIcon } from "../../atoms/icons/CheckCircleIcon"
import { Link, useSearchParams } from "react-router-dom"
import { ArrowBack } from "../../atoms/icons/ArrowBack"
import { Metadata } from "../../interfaces/SaleorTypes"
import { useCheckoutCompleteMutation } from "../../../hooks-saleor/checkoutCompleteMutation"
import ConfirmationSummary from "../../store/confirmation/ConfirmationSummary"
import BodyLarge from "../../atoms/typography/BodyLarge"
import SmallButton from "../../atoms/buttons/SmallButton"
import H1 from "../../atoms/typography/H1"
import Card from "../../atoms/Card"
import * as Sentry from "@sentry/react"
import { captureExceptionWithContext } from "../../../utils/sentry"

const HeaderContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  column-gap: 15px;
`

const ConfirmationPage: FunctionComponent = ({ ...props }) => {
  const [params, setParams] = useSearchParams()
  const checkout_id = params.get("checkout_id")
  const { checkoutCompleteMutation } = useCheckoutCompleteMutation()
  const [order, setOrder] = useState<any>()
  const [checkoutType, setCheckoutType] = useState<string>("")
  const CHECKOUT_COMPLETE_RETRY_LIMIT = 3

  async function tryCheckoutComplete(tries: number = 0) {
    if (tries < CHECKOUT_COMPLETE_RETRY_LIMIT) {
      checkoutCompleteMutation({
        variables: {
          checkoutId: checkout_id,
        },
        onCompleted: (data) => {
          if (data.checkoutComplete.order) {
            setOrder(data.checkoutComplete.order)
          } else {
            setTimeout(() => {
              tryCheckoutComplete(tries + 1)
            }, 1000)
          }
        },
      })
    } else {
      let checkoutCompleteError = new Error(`Error completing checkout: ${checkout_id}`)
      captureExceptionWithContext(checkoutCompleteError, "error completing checkout at confirmation page")
    }
  }

  const orderIsTopup = (order: any) => {
    return order?.lines?.some((line: any) => line.variant?.product?.category?.name === "Topups")
  }

  useEffect(() => {
    if (checkout_id && !order) {
      tryCheckoutComplete()
    }
    if (order) {
      let checkoutTypeMetadata = order.metadata.find((meta: Metadata) => meta.key === "checkoutType")
      setCheckoutType(checkoutTypeMetadata ? checkoutTypeMetadata.value : "direct")
    }
  }, [checkout_id, order])

  return (
    <div>
      <HorizontalSpacer spacing={3} />
      <Container>
        <Card>
          <HorizontalSpacer spacing={1} />
          <Row justify="end" nogutter>
            <Link to="/store">
              <SmallButton leftIcon={<ArrowBack />} title="Back" />
            </Link>
          </Row>

          <HorizontalSpacer spacing={4} />
          <HeaderContainer>
            <CheckCircleIcon />
            <H1>{checkoutType === "leasing" ? "Thank you for your request!" : "Thank you for your order!"}</H1>
            <HorizontalSpacer spacing={2} />
          </HeaderContainer>
          <HorizontalSpacer spacing={2} />
          {orderIsTopup(order) ? (
            <BodyLarge textAlign="center">
              Your topup is being processed. We will send you an email as soon as it is activated on your terminal. In
              the meantime, don’t hesitate to contact us if you have any questions.
            </BodyLarge>
          ) : (
            order && (
              <BodyLarge textAlign="center">
                And thank you for choosing Satcube!
                {checkoutType === "leasing"
                  ? " We will keep you informed on the progress of your leasing request. "
                  : checkoutType === "rental"
                    ? " We will keep you informed on the progress of your rental request. "
                    : " A member of our Sales team will get in touch with you very soon. You can expect your order to be delivered within 3 business days from confirmation or by the desired start date"}
                In the meantime, don’t hesitate to contact us if you have any questions or watch our{" "}
                <a href="https://youtu.be/mz_JircFuzY" target="_blank">
                  <b>demo video</b>
                </a>{" "}
                to get a first taste of how easy it is to set up your broadband connection with our terminals!
              </BodyLarge>
            )
          )}
          {order && <ConfirmationSummary order={order} checkoutType={checkoutType} />}
        </Card>
      </Container>
      <HorizontalSpacer spacing={4} />
    </div>
  )
}

export default ConfirmationPage
